/**
 * Incoming-message sound with multi-tab sync.
 * Prevents the classic bug: background tab queues/buffers `receive` events,
 * then plays N sounds when the user focuses it again (after reading elsewhere).
 */

const STORAGE_KEY = "lyke:notif-sound:v1";
const CHANNEL_NAME = "lyke-notif-sound";
const FRESH_MS = 30_000;
const RETAIN_MS = 10 * 60_000;
const SOUND_URL = "/audio/achive-sound-132273.mp3";

type Store = Record<string, number>; // messageId -> claimedAt ms

let memory: Store = {};
let channel: BroadcastChannel | null = null;
let storageListenerBound = false;
let visibilityBound = false;
let sharedAudio: HTMLAudioElement | null = null;
/** After tab focus, swallow backlog sounds (socket flush / deferred handlers). */
let suppressPlayUntil = 0;

function now() {
  return Date.now();
}

function prune(store: Store, t = now()): Store {
  const out: Store = {};
  for (const [id, at] of Object.entries(store)) {
    if (t - at < RETAIN_MS) out[id] = at;
  }
  return out;
}

function readStore(): Store {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...memory };
    const parsed = JSON.parse(raw) as Store;
    memory = prune({ ...memory, ...parsed });
    return memory;
  } catch {
    return { ...memory };
  }
}

function writeStore(store: Store) {
  memory = prune(store);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
  } catch {
    /* quota / private mode */
  }
}

function ensureListeners() {
  if (typeof window === "undefined") return;

  if (!storageListenerBound) {
    storageListenerBound = true;
    window.addEventListener("storage", (e) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try {
        memory = prune({ ...memory, ...(JSON.parse(e.newValue) as Store) });
      } catch {
        /* ignore */
      }
    });
  }

  if (!visibilityBound) {
    visibilityBound = true;
    // Capture phase: run before most socket/UI handlers on tab wake.
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.visibilityState === "visible") {
          // Buffered `receive` handlers often run right after focus.
          suppressPlayUntil = now() + 1500;
        }
      },
      true,
    );
  }

  if (!channel && typeof BroadcastChannel !== "undefined") {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (ev) => {
        const id = ev?.data?.messageId;
        if (id == null) return;
        memory = prune({ ...memory, [String(id)]: now() });
      };
    } catch {
      channel = null;
    }
  }
}

/** Returns true if this tab newly claimed the id (first across tabs). */
function claimMessageId(messageId: number | string): boolean {
  ensureListeners();
  const key = String(messageId);
  const store = readStore();
  if (store[key]) return false;
  store[key] = now();
  writeStore(store);
  try {
    channel?.postMessage({ messageId: key, at: store[key] });
  } catch {
    /* ignore */
  }
  return true;
}

function isFresh(createdAt?: string | Date | number | null): boolean {
  if (createdAt == null || createdAt === "") return true;
  const t =
    typeof createdAt === "number"
      ? createdAt
      : new Date(createdAt).getTime();
  if (!Number.isFinite(t)) return true;
  return now() - t <= FRESH_MS;
}

function getAudio() {
  if (!sharedAudio) {
    sharedAudio = new Audio(SOUND_URL);
    sharedAudio.preload = "auto";
  }
  return sharedAudio;
}

/**
 * Play new-message sound once per message across all tabs.
 * Skips when tab is hidden (avoids deferred play on focus) or message is stale.
 */
export function playIncomingMessageSound(opts: {
  messageId?: number | string | null;
  createdAt?: string | Date | number | null;
}) {
  if (typeof document === "undefined") return;

  const { messageId, createdAt } = opts;
  if (messageId == null || messageId === "") return;

  ensureListeners();

  // Never start audio while hidden — browsers often defer play until focus,
  // which sounds like a burst of notifications when switching tabs.
  // Do not claim here: the visible tab should still be able to play.
  if (document.visibilityState !== "visible") return;

  if (!isFresh(createdAt)) {
    claimMessageId(messageId);
    return;
  }

  // Tab just became visible — claim backlog ids silently (no sound burst).
  if (now() < suppressPlayUntil) {
    claimMessageId(messageId);
    return;
  }

  if (!claimMessageId(messageId)) return;

  try {
    const audio = getAudio();
    audio.currentTime = 0;
    void audio.play().catch(() => {
      /* autoplay blocked — do not retry on focus */
    });
  } catch {
    /* ignore */
  }
}
